import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI, marketsAPI, providersAPI } from '../api';

const EMPTY_REF = { company:'', name:'', email:'', phone:'' };
const FREE_EMAILS = ['gmail.com','yahoo.com','hotmail.com','outlook.com','aol.com','icloud.com','live.com','msn.com'];

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((e||'').trim());
const isValidPhone = (p) => { if(!p) return false; const d = p.replace(/\D/g,''); return d.length >= 10 && d.length <= 15; };
const isFreeEmail = (e) => FREE_EMAILS.includes((e||'').split('@')[1]?.toLowerCase());
const normPhone = (p) => { if(!p) return ''; const d = p.replace(/\D/g,''); return d.length===10?'+1'+d:d.length===11&&d[0]==='1'?'+'+d:p; };

const GIG_TYPES = [
  { slug:'data_center', label:'Data Center', icon:'🖥️', skills:['Rack & Stack','Cage Setup','Cable Management','HW Move','Config','Clean Up'] },
  { slug:'network', label:'Network', icon:'🌐', skills:['Switch Config','Router Setup','Fiber Splice','Cable Testing','Network Audit'] },
  { slug:'cabling', label:'Cabling', icon:'🔌', skills:['Cat6A','Fiber Optic','Cable Tray','Patch Panel','Cable Labeling'] },
  { slug:'laptop_desktop', label:'Laptop/Desktop', icon:'💻', skills:['Hardware Install','OS Deploy','Imaging','Break Fix','Asset Tag'] },
  { slug:'electrical', label:'Electrical', icon:'⚡', skills:['PDU Install','UPS Setup','Power Audit','Circuit Tracing','Grounding'] },
];

// Standalone Field component to prevent re-mount on re-render
function Field({ label, type='text', placeholder, required, children, hint, value, onChange, onBlur, error, showError }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}{required&&<span style={{color:'#FF4757'}}> *</span>}</label>
      {children || <input type={type} value={value||''} onChange={onChange} onBlur={onBlur} placeholder={placeholder} />}
      {hint && !showError && <div style={{fontSize:11,color:'#556677',marginTop:4}}>{hint}</div>}
      {showError && error && <div style={{fontSize:11,color:'#FF6B7A',marginTop:4}}>⚠ {error}</div>}
    </div>
  );
}

export default function RegisterProviderPage() {
  const [step, setStep]    = useState(1);
  const [markets, setMarkets] = useState([]);
  const [certs, setCerts]   = useState([]);
  const [err, setErr]       = useState('');
  const [busy, setBusy]     = useState(false);
  const [done, setDone]     = useState(false);
  const [touched, setTouched] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const nav = useNavigate();

  const [form, setForm] = useState({
    real_name:'', email:'', password:'', phone:'', market_id:'',
    specialty:'', experience_years:'', rate_hr:'', profile_headline:'',
    skills:'', insurance_policy:'', insurance_provider:'',
    insurance_amount:'', certifications:[], consent:false,
    // New blueprint fields
    gig_types:[], selected_skills:[],
    travel_surcharge:'', min_hours:'1',
    has_tools:false, has_ppe:false, open_to_video_call:true,
    id_type:'', background_check_consent:false,
    platform_fee_consent:false,
  });

  const [refs, setRefs] = useState([{ ...EMPTY_REF }, { ...EMPTY_REF }]);
  const [refErrors, setRefErrors] = useState([{}, {}]);

  useEffect(()=>{
    marketsAPI.list().then(r=>setMarkets(r.data)).catch(()=>{});
    providersAPI.certList().then(r=>setCerts(r.data)).catch(()=>{});
  },[]);

  const validateField = (key, value) => {
    switch(key) {
      case 'real_name': return !value?.trim() ? 'Full name required' : '';
      case 'email': return !value ? 'Email required' : !isValidEmail(value) ? 'Invalid email format' : '';
      case 'password': return !value ? 'Password required' : value.length < 8 ? 'Min 8 characters' : '';
      case 'phone': return !value ? 'Phone required' : !isValidPhone(value) ? 'Must be at least 10 digits' : '';
      case 'market_id': return !value ? 'Please select a market' : '';
      case 'specialty': return !value?.trim() ? 'Specialty required' : '';
      case 'experience_years': return !value ? 'Required' : parseInt(value) < 10 ? 'Min 10 years required' : '';
      case 'rate_hr': return !value ? 'Rate required' : parseFloat(value) <= 0 ? 'Must be greater than 0' : '';
      case 'insurance_policy': return !value?.trim() ? 'Insurance policy number required' : '';
      case 'id_type': return !value ? 'Please select an ID type' : '';
      default: return '';
    }
  };

  const upd = k => e => setForm(f=>({...f,[k]:e.target.value}));
  const updBool = k => e => setForm(f=>({...f,[k]:e.target.checked}));

  const blur = k => () => {
    setTouched(t=>({...t,[k]:true}));
    setFieldErrors(fe=>({...fe,[k]:validateField(k,form[k]||'')}));
  };

  const toggleGigType = (slug) => {
    setForm(f => {
      const types = f.gig_types.includes(slug) ? f.gig_types.filter(g=>g!==slug) : [...f.gig_types, slug];
      return {...f, gig_types: types};
    });
  };

  const toggleSkill = (skill) => {
    setForm(f => {
      const skills = f.selected_skills.includes(skill) ? f.selected_skills.filter(s=>s!==skill) : [...f.selected_skills, skill];
      return {...f, selected_skills: skills};
    });
  };

  const updCert = id => setForm(f=>({...f,
    certifications: f.certifications.includes(id) ? f.certifications.filter(c=>c!==id) : [...f.certifications, id]
  }));

  const updRef = (i, k) => e => setRefs(r => r.map((ref,idx) => idx===i ? {...ref,[k]:e.target.value} : ref));

  const blurRef = (i, k) => () => {
    const val = refs[i][k];
    const newErr = validateRefField(i, k, val);
    setRefErrors(re => re.map((err,idx) => idx===i ? {...err,[k]:newErr} : err));
  };

  const validateRefField = (i, k, val) => {
    switch(k) {
      case 'company': return !val?.trim() ? 'Company required' : '';
      case 'name':
        if (!val?.trim()) return 'Contact name required';
        const dupName = refs.some((r,idx) => idx!==i && r.name.toLowerCase()===val.toLowerCase());
        return dupName ? 'Duplicate name' : '';
      case 'email':
        if (!val?.trim()) return 'Email required';
        if (!isValidEmail(val)) return 'Invalid email format';
        if (isFreeEmail(val)) return 'Use official company email';
        if (val.toLowerCase() === form.email.toLowerCase()) return 'Cannot use your own email';
        const dupEmail = refs.some((r,idx) => idx!==i && r.email.toLowerCase()===val.toLowerCase());
        return dupEmail ? 'Duplicate email' : '';
      case 'phone':
        if (!val?.trim()) return 'Phone required';
        if (!isValidPhone(val)) return 'Must be 10+ digits';
        if (normPhone(val) === normPhone(form.phone)) return 'Cannot use your own phone';
        return '';
      default: return '';
    }
  };

  const addRef = () => { setRefs(r => [...r, { ...EMPTY_REF }]); setRefErrors(re => [...re, {}]); };
  const removeRef = i => { setRefs(r => r.filter((_,idx) => idx!==i)); setRefErrors(re => re.filter((_,idx) => idx!==i)); };

  const validateAllRefs = () => {
    const newRefErrors = refs.map((ref, i) => ({
      company: validateRefField(i,'company',ref.company),
      name: validateRefField(i,'name',ref.name),
      email: validateRefField(i,'email',ref.email),
      phone: validateRefField(i,'phone',ref.phone),
    }));
    setRefErrors(newRefErrors);
    return newRefErrors.every(e => Object.values(e).every(v => !v));
  };

  const validateStep = (n) => {
    if (n === 1) {
      if (form.gig_types.length === 0) { setErr('Please select at least one gig type'); return false; }
      setErr(''); return true;
    }
    if (n === 2) {
      const keys = ['real_name','email','password','phone','market_id','specialty','experience_years','id_type'];
      const errors = {};
      keys.forEach(k => { errors[k] = validateField(k, form[k]||''); });
      setFieldErrors(fe=>({...fe,...errors}));
      setTouched(t=>{ const nt={...t}; keys.forEach(k=>nt[k]=true); return nt; });
      if (Object.values(errors).some(v=>v)) { setErr('Please fix the errors above'); return false; }
      if (!form.background_check_consent) { setErr('Background check consent required'); return false; }
      setErr(''); return true;
    }
    if (n === 3) {
      if (!validateAllRefs()) { setErr('Please fix reference errors above'); return false; }
      if (!form.insurance_policy) { setFieldErrors(fe=>({...fe,insurance_policy:'Required'})); setTouched(t=>({...t,insurance_policy:true})); return false; }
      if (!form.consent) { setErr('Please check the consent box'); return false; }
      setErr(''); return true;
    }
    if (n === 4) {
      if (!form.rate_hr) { setFieldErrors(fe=>({...fe,rate_hr:'Rate required'})); setTouched(t=>({...t,rate_hr:true})); return false; }
      if (!form.platform_fee_consent) { setErr('Please accept the platform fee agreement'); return false; }
      setErr(''); return true;
    }
    return true;
  };

  const submit = async () => {
    if (!validateStep(4)) return;
    setBusy(true); setErr('');
    try {
      await authAPI.providerRegister({
        ...form,
        experience_years: parseInt(form.experience_years),
        rate_hr: parseFloat(form.rate_hr),
        travel_surcharge: parseFloat(form.travel_surcharge) || 0,
        min_hours: parseFloat(form.min_hours) || 1,
        skills: form.selected_skills.length > 0 ? form.selected_skills : form.skills.split(',').map(s=>s.trim()).filter(Boolean),
        references: refs.map(r => `${r.company} | ${r.name} | ${r.email} | ${r.phone}`),
      });
      setDone(true);
    } catch(e) { setErr(e.response?.data?.error||'Registration failed'); }
    setBusy(false);
  };

  if (done) return (
    <div className="page-container" style={{maxWidth:480,paddingTop:60,textAlign:'center'}}>
      <div style={{fontSize:52,marginBottom:14}}>✉️</div>
      <div className="section-title" style={{marginBottom:8}}>Check Your Email!</div>
      <div style={{color:'#8899AA',fontSize:15,lineHeight:1.7,marginBottom:16}}>
        We sent a verification link to your email address. Please click it to confirm your account.
      </div>
      <div style={{background:'rgba(109,190,69,0.08)',border:'1px solid rgba(109,190,69,0.2)',borderRadius:8,padding:'14px 16px',marginBottom:28,fontSize:13,color:'#AAB8C8',textAlign:'left'}}>
        ✅ &nbsp;Once verified, your application will be reviewed within 1–2 business days.<br/><br/>
        📞 &nbsp;We'll contact your references and verify your insurance.<br/><br/>
        🎉 &nbsp;You'll receive an approval email once everything checks out.
      </div>
      <button className="btn btn-primary btn-lg" onClick={()=>nav('/')}>Back to Home</button>
    </div>
  );

  const steps = ['Gig Type','Your Profile','Verification','Rates & Terms'];
  const refInputErr = (i,k) => refErrors[i]?.[k] ? <div style={{fontSize:11,color:'#FF6B7A',marginTop:2}}>⚠ {refErrors[i][k]}</div> : null;
  const refCardStyle = {background:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:10,padding:'16px',marginBottom:12};
  const refLabelStyle = {fontSize:10,textTransform:'uppercase',letterSpacing:1,color:'#556677',fontWeight:600,marginBottom:4,display:'block'};

  const availableSkills = GIG_TYPES.filter(g => form.gig_types.includes(g.slug)).flatMap(g => g.skills);
  const uniqueSkills = [...new Set(availableSkills)];

  return (
    <div className="page-container" style={{maxWidth:560,paddingTop:50}}>
      <div className="section-title" style={{marginBottom:4}}>Join as a Provider</div>
      <div className="section-sub" style={{marginBottom:24}}>Minimum 10 years experience · Insurance required · References verified</div>

      {/* Step indicator */}
      <div style={{display:'flex',gap:0,marginBottom:28}}>
        {steps.map((s,i)=>(
          <div key={i} style={{flex:1,textAlign:'center'}}>
            <div style={{height:3,background:step>i+1?'#1E7FD4':step===i+1?'#1E7FD4':'rgba(255,255,255,0.08)',borderRadius:2,marginBottom:6,opacity:step===i+1?1:step>i+1?1:0.3}}/>
            <div style={{fontSize:10,color:step===i+1?'#1E7FD4':step>i+1?'#445566':'#334455',fontWeight:600,textTransform:'uppercase',letterSpacing:0.5}}>{i+1}. {s}</div>
          </div>
        ))}
      </div>

      {err && <div className="alert alert-error">{err}</div>}

      {/* STEP 1: Gig Type Selection */}
      {step===1 && <>
        <div className="form-group">
          <label className="form-label">Select Your Gig Type(s) <span style={{color:'#FF4757'}}>*</span></label>
          <div style={{fontSize:12,color:'#556677',marginBottom:12}}>Select all areas you can work in</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:20}}>
            {GIG_TYPES.map(g => (
              <button key={g.slug} type="button" onClick={()=>toggleGigType(g.slug)}
                style={{padding:'16px 8px',borderRadius:10,border:'none',cursor:'pointer',transition:'all 0.2s',textAlign:'center',
                  background:form.gig_types.includes(g.slug)?'rgba(30,127,212,0.2)':'rgba(255,255,255,0.04)',
                  outline:form.gig_types.includes(g.slug)?'2px solid #1E7FD4':'2px solid transparent'}}>
                <div style={{fontSize:28,marginBottom:6}}>{g.icon}</div>
                <div style={{fontSize:12,fontWeight:600,color:form.gig_types.includes(g.slug)?'#1E7FD4':'#8899AA'}}>{g.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Skill checkboxes based on selected gig types */}
        {uniqueSkills.length > 0 && (
          <div className="form-group">
            <label className="form-label">Select Your Skills</label>
            <div style={{display:'flex',flexWrap:'wrap',gap:8,marginTop:6}}>
              {uniqueSkills.map(skill => (
                <button key={skill} type="button" onClick={()=>toggleSkill(skill)}
                  style={{padding:'6px 12px',borderRadius:6,fontSize:12,fontWeight:600,border:'none',cursor:'pointer',transition:'all 0.2s',
                    background:form.selected_skills.includes(skill)?'rgba(109,190,69,0.2)':'rgba(255,255,255,0.05)',
                    color:form.selected_skills.includes(skill)?'#6DBE45':'#8899AA',
                    outline:form.selected_skills.includes(skill)?'1px solid rgba(109,190,69,0.4)':'1px solid transparent'}}>
                  {skill}
                </button>
              ))}
            </div>
          </div>
        )}

        <button className="btn btn-primary" style={{width:'100%'}} onClick={()=>{ if(validateStep(1)) setStep(2); }}>
          Next: Your Profile →
        </button>
      </>}

      {/* STEP 2: Personal Profile & Identity */}
      {step===2 && <>
        <Field label="Full Legal Name" required value={form.real_name} onChange={upd('real_name')} onBlur={blur('real_name')} placeholder="Your full name (kept private until hired)" error={fieldErrors.real_name} showError={touched.real_name} />
        <div className="form-row">
          <Field label="Email" type="email" required value={form.email} onChange={upd('email')} onBlur={blur('email')} placeholder="you@email.com" error={fieldErrors.email} showError={touched.email} />
          <Field label="Phone" required value={form.phone} onChange={upd('phone')} onBlur={blur('phone')} placeholder="+1 (555) 000-0000" error={fieldErrors.phone} showError={touched.phone} />
        </div>
        <Field label="Password" type="password" required value={form.password} onChange={upd('password')} onBlur={blur('password')} placeholder="Min 8 characters" error={fieldErrors.password} showError={touched.password} />
        <Field label="Primary Market" required error={fieldErrors.market_id} showError={touched.market_id}>
          <select value={form.market_id} onChange={upd('market_id')} onBlur={blur('market_id')}>
            <option value="">Select market...</option>
            {markets.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </Field>
        <Field label="Primary Specialty" required value={form.specialty} onChange={upd('specialty')} onBlur={blur('specialty')} placeholder="e.g. Data Center Infrastructure" error={fieldErrors.specialty} showError={touched.specialty} />
        <Field label="Years of Experience" type="number" required value={form.experience_years} onChange={upd('experience_years')} onBlur={blur('experience_years')} placeholder="e.g. 14" hint="Minimum 10 years required" error={fieldErrors.experience_years} showError={touched.experience_years} />

        {/* Government ID */}
        <div className="form-group">
          <label className="form-label">Government ID Type <span style={{color:'#FF4757'}}>*</span></label>
          <div style={{display:'flex',gap:10,marginTop:6}}>
            {['Driver\'s License','Passport','State ID'].map(t => (
              <button key={t} type="button" onClick={()=>{setForm(f=>({...f,id_type:t})); setTouched(tc=>({...tc,id_type:true})); setFieldErrors(fe=>({...fe,id_type:''}));}}
                style={{flex:1,padding:'10px 8px',borderRadius:8,border:'none',cursor:'pointer',transition:'all 0.2s',fontSize:12,fontWeight:600,
                  background:form.id_type===t?'rgba(30,127,212,0.2)':'rgba(255,255,255,0.04)',
                  color:form.id_type===t?'#1E7FD4':'#8899AA',
                  outline:form.id_type===t?'2px solid #1E7FD4':'2px solid transparent'}}>
                {t==="Driver's License"?'🪪':t==='Passport'?'📕':'🪪'} {t}
              </button>
            ))}
          </div>
          {fieldErrors.id_type && touched.id_type && <div style={{fontSize:11,color:'#FF6B7A',marginTop:4}}>⚠ {fieldErrors.id_type}</div>}
          <div style={{fontSize:11,color:'#556677',marginTop:6}}>ID will be verified by our team — not stored publicly</div>
        </div>

        {/* Background Check */}
        <div style={{background:'rgba(255,165,0,0.06)',border:'1px solid rgba(255,165,0,0.2)',borderRadius:8,padding:'14px 16px',marginBottom:20}}>
          <div style={{fontSize:13,fontWeight:600,color:'#F0C060',marginBottom:8}}>🔍 Background Check Consent</div>
          <div style={{fontSize:13,color:'#AAB8C8',marginBottom:12}}>We conduct background screenings on all providers. Fees will be disclosed transparently before running.</div>
          <div style={{display:'flex',gap:10}}>
            <button type="button" onClick={()=>setForm(f=>({...f,background_check_consent:true}))}
              style={{flex:1,padding:'8px',borderRadius:7,border:'none',cursor:'pointer',fontSize:13,fontWeight:600,
                background:form.background_check_consent?'rgba(109,190,69,0.2)':'rgba(255,255,255,0.05)',
                color:form.background_check_consent?'#6DBE45':'#8899AA',
                outline:form.background_check_consent?'1px solid #6DBE45':'1px solid transparent'}}>
              ✓ Yes, I consent
            </button>
            <button type="button" onClick={()=>setForm(f=>({...f,background_check_consent:false}))}
              style={{flex:1,padding:'8px',borderRadius:7,border:'none',cursor:'pointer',fontSize:13,fontWeight:600,
                background:!form.background_check_consent?'rgba(255,71,87,0.1)':'rgba(255,255,255,0.05)',
                color:!form.background_check_consent?'#FF4757':'#8899AA',
                outline:!form.background_check_consent?'1px solid rgba(255,71,87,0.3)':'1px solid transparent'}}>
              ✗ No
            </button>
          </div>
        </div>

        <div style={{display:'flex',gap:10}}>
          <button className="btn btn-ghost" onClick={()=>{setErr('');setStep(1);}}>← Back</button>
          <button className="btn btn-primary" style={{flex:1}} onClick={()=>{ if(validateStep(2)) setStep(3); }}>Next: Verification →</button>
        </div>
      </>}

      {/* STEP 3: Certifications, References, Insurance */}
      {step===3 && <>
        {/* Certifications */}
        <div style={{marginBottom:18}}>
          <label className="form-label">Certifications (select all that apply)</label>
          <div style={{display:'flex',gap:7,flexWrap:'wrap',marginTop:4}}>
            {certs.map(c=>(
              <button key={c.id} type="button" onClick={()=>updCert(c.id)}
                style={{padding:'6px 12px',borderRadius:6,fontSize:12,fontWeight:600,border:'none',cursor:'pointer',transition:'all 0.2s',
                  background:form.certifications.includes(c.id)?'rgba(0,194,255,0.2)':'rgba(255,255,255,0.05)',
                  color:form.certifications.includes(c.id)?'#1E7FD4':'#8899AA',
                  outline:form.certifications.includes(c.id)?'1px solid rgba(0,194,255,0.4)':'1px solid transparent'}}>
                {c.name}
              </button>
            ))}
          </div>
        </div>

        {/* References */}
        <div className="form-group">
          <label className="form-label">Employment References <span style={{color:'#FF4757'}}>*minimum 2</span></label>
          <div style={{fontSize:12,color:'#556677',marginBottom:10}}>Each reference must have an official company email — no Gmail/Yahoo etc.</div>
          {refs.map((ref, i) => (
            <div key={i} style={refCardStyle}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                <span style={{fontSize:12,fontWeight:700,color:'#8899AA',textTransform:'uppercase',letterSpacing:1}}>Reference {i+1}</span>
                {refs.length > 2 && (
                  <button type="button" onClick={()=>removeRef(i)}
                    style={{background:'rgba(255,71,87,0.1)',border:'1px solid rgba(255,71,87,0.2)',color:'#FF4757',borderRadius:5,padding:'2px 8px',fontSize:11,cursor:'pointer'}}>Remove</button>
                )}
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                <div><label style={refLabelStyle}>Company Name</label><input value={ref.company} onChange={updRef(i,'company')} onBlur={blurRef(i,'company')} placeholder="e.g. Equinix" style={{marginBottom:0}}/>{refInputErr(i,'company')}</div>
                <div><label style={refLabelStyle}>Contact Name</label><input value={ref.name} onChange={updRef(i,'name')} onBlur={blurRef(i,'name')} placeholder="e.g. John Smith" style={{marginBottom:0}}/>{refInputErr(i,'name')}</div>
                <div><label style={refLabelStyle}>Official Email</label><input type="email" value={ref.email} onChange={updRef(i,'email')} onBlur={blurRef(i,'email')} placeholder="contact@company.com" style={{marginBottom:0}}/>{refInputErr(i,'email')}</div>
                <div><label style={refLabelStyle}>Phone Number</label><input value={ref.phone} onChange={updRef(i,'phone')} onBlur={blurRef(i,'phone')} placeholder="214-555-0100" style={{marginBottom:0}}/>{refInputErr(i,'phone')}</div>
              </div>
            </div>
          ))}
          {refs.length < 5 && (
            <button type="button" onClick={addRef} style={{width:'100%',padding:'8px',background:'rgba(255,255,255,0.03)',border:'1px dashed rgba(255,255,255,0.12)',borderRadius:8,color:'#556677',fontSize:13,cursor:'pointer',marginTop:4}}>
              + Add Another Reference
            </button>
          )}
        </div>

        {/* Insurance */}
        <div className="form-group">
          <label className="form-label">Insurance Policy Number <span style={{color:'#FF4757'}}>*</span></label>
          <input value={form.insurance_policy} onChange={upd('insurance_policy')} onBlur={blur('insurance_policy')} placeholder="Policy number" />
          {fieldErrors.insurance_policy && touched.insurance_policy && <div style={{fontSize:11,color:'#FF6B7A',marginTop:4}}>⚠ {fieldErrors.insurance_policy}</div>}
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Insurance Provider</label><input value={form.insurance_provider} onChange={upd('insurance_provider')} placeholder="e.g. Hiscox" /></div>
          <div className="form-group"><label className="form-label">Coverage Amount</label><input value={form.insurance_amount} onChange={upd('insurance_amount')} placeholder="e.g. $1,000,000" /></div>
        </div>

        <div style={{display:'flex',alignItems:'flex-start',gap:10,padding:'12px 14px',background:'rgba(0,194,255,0.05)',border:'1px solid rgba(0,194,255,0.15)',borderRadius:8,marginBottom:20,cursor:'pointer'}}
          onClick={()=>setForm(f=>({...f,consent:!f.consent}))}>
          <input type="checkbox" checked={form.consent} onChange={()=>{}} style={{width:'auto',marginTop:2,accentColor:'#1E7FD4'}} />
          <div style={{fontSize:13,color:'#AAB8C8'}}>I consent to background verification and authorize iTekPros to contact my references and verify my insurance.</div>
        </div>

        <div style={{display:'flex',gap:10}}>
          <button className="btn btn-ghost" onClick={()=>{setErr('');setStep(2);}}>← Back</button>
          <button className="btn btn-primary" style={{flex:1}} onClick={()=>{ if(validateStep(3)) setStep(4); }}>Next: Rates & Terms →</button>
        </div>
      </>}

      {/* STEP 4: Rates, Logistics & Terms */}
      {step===4 && <>
        {/* Rate guidance */}
        <div style={{background:'rgba(0,194,255,0.06)',border:'1px solid rgba(0,194,255,0.2)',borderRadius:8,padding:14,marginBottom:18}}>
          <div style={{fontSize:10,color:'#1E7FD4',textTransform:'uppercase',letterSpacing:1,marginBottom:4}}>📊 Market Rate Guidance</div>
          <div style={{color:'#F0F6FF',fontSize:14}}>Check the market ticker for current rates. Certified providers typically earn premium rates.</div>
        </div>

        <div className="form-row">
          <Field label="Hourly Rate (USD)" type="number" required value={form.rate_hr} onChange={upd('rate_hr')} onBlur={blur('rate_hr')} placeholder="e.g. 95" error={fieldErrors.rate_hr} showError={touched.rate_hr} />
          <Field label="Travel Surcharge (USD)" type="number" value={form.travel_surcharge} onChange={upd('travel_surcharge')} onBlur={blur('travel_surcharge')} placeholder="e.g. 50" hint="Flat fee to cover travel to site" error={fieldErrors.travel_surcharge} showError={touched.travel_surcharge} />
        </div>

        <div className="form-row">
          <Field label="Minimum Hours / Engagement" type="number" value={form.min_hours} onChange={upd('min_hours')} onBlur={blur('min_hours')} placeholder="e.g. 2" hint="Min billable hours per job" error={fieldErrors.min_hours} showError={touched.min_hours} />
          <Field label="Profile Headline" value={form.profile_headline} onChange={upd('profile_headline')} onBlur={blur('profile_headline')} placeholder="e.g. BICSI-certified DC expert, 14 years" error={fieldErrors.profile_headline} showError={touched.profile_headline} />
        </div>

        {/* Logistics checkboxes */}
        <div className="form-group">
          <label className="form-label">On-Site Readiness</label>
          <div style={{display:'flex',flexDirection:'column',gap:10,marginTop:8}}>
            {[
              {key:'has_tools', label:'I will carry my own tools and equipment'},
              {key:'has_ppe', label:'I have required PPE (safety vest, steel-toe shoes)'},
              {key:'open_to_video_call', label:'I am open to a 15-minute video scope call before site visits'},
            ].map(item => (
              <label key={item.key} style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',fontSize:13,color:'#AAB8C8'}}>
                <input type="checkbox" checked={form[item.key]} onChange={updBool(item.key)} style={{width:'auto',accentColor:'#1E7FD4'}} />
                {item.label}
              </label>
            ))}
          </div>
        </div>

        {/* Platform fee agreement */}
        <div style={{background:'rgba(255,165,0,0.06)',border:'2px solid rgba(255,165,0,0.3)',borderRadius:10,padding:'18px',marginBottom:20}}>
          <div style={{fontSize:14,fontWeight:700,color:'#F0C060',marginBottom:10}}>💰 Platform Commission Agreement</div>
          <div style={{fontSize:14,color:'#F0F6FF',marginBottom:4}}>iTekPros charges <strong style={{color:'#6DBE45'}}>20% of gross billing</strong> from each completed gig.</div>
          <div style={{fontSize:12,color:'#8899AA',marginBottom:14}}>Example: If you earn $500 for a job, iTekPros retains $100 and you receive $400.</div>
          <label style={{display:'flex',alignItems:'flex-start',gap:10,cursor:'pointer',fontSize:13,color:'#AAB8C8'}}>
            <input type="checkbox" checked={form.platform_fee_consent} onChange={updBool('platform_fee_consent')} style={{width:'auto',marginTop:2,accentColor:'#6DBE45'}} />
            I understand and agree to the 20% platform commission fee on all completed gigs
          </label>
        </div>

        <div style={{display:'flex',gap:10}}>
          <button className="btn btn-ghost" onClick={()=>{setErr('');setStep(3);}}>← Back</button>
          <button className="btn btn-green" style={{flex:1}} onClick={submit} disabled={busy}>
            {busy?<><span className="spinner"/>  Submitting...</>:'Submit Application ✓'}
          </button>
        </div>
        <div style={{fontSize:11,color:'#445566',marginTop:12,textAlign:'center'}}>
          Profile reviewed within 3 business days. References and insurance will be verified.
        </div>
      </>}

      <div style={{textAlign:'center',marginTop:16,fontSize:13,color:'#556677'}}>
        Already have an account? <Link to="/login">Sign in</Link>
      </div>
    </div>
  );
}
